<script lang="ts">
  import AvailabilityMask from "../../_shared/_modules/availability-mask"
  import CalendarEventCard from "../../../../_shared/_modules/calendar-event-card"
  import SoftSlotButton from "./_modules/soft-slot-button"
  import type {
    BoardActions,
    BoardColumn,
    BoardEvent,
    BoardLayoutConfig,
    BoardMovePreview,
    SoftSlot,
    SoftSlotDisplayMode,
  } from "../../../../../../../../_logic/calendar-types"

  type BoardPropertyRef = NonNullable<BoardEvent["property"]>

  const {
    column,
    softSlots = [],
    isDayOff = false,
    flexSlotsDisabled = false,
    viewStartMinutes,
    viewEndMinutes,
    layout,
    actions,
    formatAddress,
    showEventTooltips = true,
    showAppointmentTagTooltip = true,
    displayMode = "interactive",
    readOnly = false,
    allowSlotCreation = true,
    hiddenEventId = null,
    movePreview = null,
    canDirectlyEditEvents = false,
    canMoveEvent = () => false,
    onMoveStart = () => {},
  }: {
    column: BoardColumn
    softSlots: SoftSlot[]
    isDayOff?: boolean
    flexSlotsDisabled?: boolean
    viewStartMinutes: number
    viewEndMinutes: number
    layout: BoardLayoutConfig
    actions: BoardActions
    formatAddress: (property?: BoardPropertyRef) => string
    showEventTooltips?: boolean
    showAppointmentTagTooltip?: boolean
    displayMode?: SoftSlotDisplayMode
    readOnly?: boolean
    allowSlotCreation?: boolean
    hiddenEventId?: string | null
    movePreview?: BoardMovePreview | null
    canDirectlyEditEvents?: boolean
    canMoveEvent?: (item: BoardEvent) => boolean
    onMoveStart?: (item: BoardEvent, event: PointerEvent) => void
  } = $props()

  const slotHeight = $derived(layout.slotHeight)
  const slotMinutes = $derived(layout.slotMinutes)
  const visibleSlots = $derived(
    displayMode === "occupied-only"
      ? softSlots.filter((slot) => Boolean(slot.softEvent))
      : softSlots,
  )
</script>

<div
  class="soft-area"
  class:off-day={isDayOff}
  class:flex-disabled={flexSlotsDisabled}
  aria-label={flexSlotsDisabled ? "Flextermine deaktiviert" : undefined}
>
  <AvailabilityMask
    availability={column.availability}
    {viewStartMinutes}
    {viewEndMinutes}
    {slotMinutes}
    {slotHeight}
    ariaHidden={true}
  />
  {#if displayMode === "interactive" && !flexSlotsDisabled}
    {#if visibleSlots.length === 0}
      <div class="soft-empty">Keine Flexslots</div>
    {:else}
      {#each visibleSlots as slot, idx (slot.softEvent?.event?.id ?? `${slot.startMinutes}:${slot.endMinutes}:${idx}`)}
        <SoftSlotButton
          {slot}
          index={idx}
          day={column.day}
          employeeId={column.employee?.id}
          employeeLabel={column.employee?.label ?? ""}
          calendarSyncEnabled={Boolean(column.calendarSyncEnabled)}
          {formatAddress}
          {showEventTooltips}
          {showAppointmentTagTooltip}
          {actions}
          {readOnly}
          {allowSlotCreation}
          columnId={column.id}
          {hiddenEventId}
          allowMoveInteractions={canDirectlyEditEvents}
          {canMoveEvent}
          {onMoveStart}
        />
      {/each}
    {/if}
  {:else if displayMode === "occupied-only"}
    {#each visibleSlots as slot, idx (slot.softEvent?.event?.id ?? `${slot.startMinutes}:${slot.endMinutes}:${idx}`)}
      <SoftSlotButton
        {slot}
        index={idx}
        day={column.day}
        employeeId={column.employee?.id}
        employeeLabel={column.employee?.label ?? ""}
        calendarSyncEnabled={Boolean(column.calendarSyncEnabled)}
        {formatAddress}
        {showEventTooltips}
        {showAppointmentTagTooltip}
        {actions}
        {readOnly}
        {allowSlotCreation}
        columnId={column.id}
        {hiddenEventId}
        allowMoveInteractions={canDirectlyEditEvents}
        {canMoveEvent}
        {onMoveStart}
      />
    {/each}
  {/if}
  {#if movePreview}
    <CalendarEventCard
      item={movePreview.item}
      minEventHeight={layout.minEventHeight}
      {formatAddress}
      employeeLabel={column.employee?.label ?? ""}
      calendarSyncEnabled={Boolean(column.calendarSyncEnabled)}
      readOnly={true}
      showEventTooltips={false}
      overridePosition={{ top: movePreview.top, height: movePreview.height }}
      useRouteInsets={false}
      isActive={true}
      preview={true}
      isPast={false}
      scrollAxes="both"
      onOpen={() => undefined}
    />
  {/if}
</div>

<style lang="less">
  .soft-area {
    position: relative;
    background: color-mix(in srgb, var(--cal-surface, #eceaea) 65%, transparent);
    border-right: 1px solid var(--cal-border, rgba(0, 0, 0, 0.08));
    --mask-offset: calc(-1 * var(--soft-col-width, 100px));

    &.off-day,
    &.flex-disabled {
      background:
        repeating-linear-gradient(
          135deg,
          color-mix(in srgb, var(--cal-accent-soft, #ad514c) 32%, transparent) 0,
          color-mix(in srgb, var(--cal-accent-soft, #ad514c) 32%, transparent) 14px,
          color-mix(in srgb, var(--cal-accent-soft, #ad514c) 18%, transparent) 14px,
          color-mix(in srgb, var(--cal-accent-soft, #ad514c) 18%, transparent) 28px
        ),
        color-mix(in srgb, var(--cal-surface, #eceaea) 55%, transparent);
      background-position: var(--mask-offset, 0px) 0;
    }

    .soft-empty {
      position: absolute;
      inset: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 0.8rem;
      color: var(--cal-text-muted, #6d97b0);
    }
  }
</style>
